<template>
<div>

	<div v-if="locationBanner" id="nudge" class="alert alert-warning mb-0">
		<strong>Uh oh! We can't detect your location.</strong> Locations are stored with checkins and help with route navigation. Please authorize or re-enable location tracking for this site.
	</div>

	<page-loading v-if="!data_loaded" />

	<div v-else-if="error" class="center-me">
		<div class="d-flex">
			<div class="pt-4 mr-3 text-gray-400 text-5xl">
				<i class="fal fa-bomb"></i>
			</div>
			<div>
				<div class="font-weight-bold text-7xl text-gray-400">Uh oh</div>
				<div class="text-gray-500 text-2xl">There was an error loading the application.</div>
				<div class="text-gray-500">If refreshing does not work, please contact your account manager.</div>
			</div>
		</div>
	</div>

	<div v-else class="d-flex">
		<div id="dashboard" class="container-fluid" :class="{'with-location-warning' : locationBanner }">
			<div class="row">
				<div class="col-12">

					<div v-if="dashboard.promo" class="mb-4 rounded" style="position:relative; background:#525e67;" data-test="dashboardPromo">
						<div class="announcement-box text-white">
							<div class="announcement-icon">
								<i class="fad fa-megaphone text-7xl"></i>
							</div>
							<div class="text-center leading-tight" style="position: relative; z-index:10;">
								<div class="mb-2 text-3xl font-weight-bold" v-html="dashboard.promo.title" />
								<div class="text-2xl leading-tight" v-html="dashboard.promo.message" />
							</div>
						</div>
						<div>
							<img 
								:src="dashboard.promo.image_url"
								style="z-index:0; position:absolute;"
								:style="dashboard.promo.image_style ? dashboard.promo.image_style : 'right:-20px; top:-20px; max-width: 220px; max-height:170px;'">
						</div>
					</div>


					<h1>Goal Progress</h1>
					<div class="card" style="">
						<div class="card-goals">
							<dashboard-goals :goals="dashboard.goals" />
						</div>
						<div class="card-footer">
							Add new, edit existing, or view all facility goals in the <router-link :to="{ name: 'goals.index' }">goal manager</router-link>.
						</div>
					</div>
					

					<h1>Program Activity</h1>
					<div class="card program-activity p-35">						
						<div class="row">
							<div class="col-lg-4 col-md-6 mb-4">
								<dashboard-achievement />
							</div>

							<div class="col-lg-4 col-md-6 mb-4">
								<dashboard-quick-tips :quicktips="dashboard.quicktips" />
							</div>

							<div class="col-lg-4 leaderboard">
								<div class="section-label">Checkins Leaderboard</div>
								<div 
									v-for="(leader, index) in dashboard.leaders"
									class="user d-flex align-items-center px-3 py-2 rounded"
									:class="{ 'current-user' : leader.id == user.id }" >
									<div style="width:16px;">{{ Number(index) + 1 }}</div>
									<div class="rounded-circle circle-cropped avatar-sm mr-2">
										<img :src="leader.photo_url" class="img-fluid">
									</div>
									<span class="flex-grow-1">{{ leader.display_name }}</span>
									<div class="checkins-count font-weight-bold text-align-right">{{ leader.checkins_count }}</div>
								</div>
							</div>
							
						</div>

						<div class="border-top mt-4 mb-4 w-100"></div>

						<dashboard-referrals />

					</div>
				</div>

			</div>
		</div>


		<section id="rightside" :class="{'with-location-warning' : locationBanner }">
			<div id="rightside-hide" class="rightside-toggle" @click="hideRightSide()">
				<i class="text-gray-500 text-xl far fa-angle-double-right" style="line-height: 38px;"></i>
			</div>
			<div id="rightside-show" class="rightside-toggle" @click="showRightSide()">
				<i class="text-gray-500 text-xl far fa-angle-double-left" style="line-height: 38px;"></i>
			</div>

			<section class="text-gray-500 font-weight-bold bg-white border-bottom">
				<img :src="user.client.photo_url" class="img-fluid">
				<div class="d-flex px-4 pb-3">
					<div style="margin-top:-40px" class="rounded-circle circle-cropped avatar-2xl">
						<img :src="user.photo_url" class="img-fluid">
					</div>

					<div class="ml-2 mt-3">
						<div class="text-lg leading-none">Welcome back</div>
						<div class="text-xl">{{ user.first_name }}!</div>
						<img :src="user.client.logo_url" class="img-fluid py-3" style="max-width:120px; max-height:70px;">
					</div>
				</div>
			</section>

			<dashboard-routes />

			<dashboard-campaigns />

			<dashboard-tasks :tasks="dashboard.user.tasks" />
 
		</section>
	</div>
</div>
</template>

<script>

	import { mapState } from 'vuex'
    import BreadcrumbsManager from '../helpers/BreadcrumbManager';

	import DashboardGoals from '../components/DashboardGoals'
	import DashboardAchievement from '../components/DashboardAchievement'
	import DashboardQuickTips from '../components/DashboardQuickTips'
	import DashboardRoutes from '../components/DashboardRoutes'
	import DashboardCampaigns from '../components/DashboardCampaigns'
	import DashboardReferrals from '../components/DashboardReferrals'
	import DashboardTasks from '../components/DashboardTasks'

    export default {
    	mixins: [BreadcrumbsManager],

    	components: {
    		DashboardGoals,
    		DashboardRoutes,
    		DashboardCampaigns,
    		DashboardTasks,
    		DashboardAchievement,
    		DashboardQuickTips,
    		DashboardReferrals
    	},

    	data() {
			return {
    			dashboard: {
    				promo: null,
    				user: {
    					tasks: [],
    				},
    			},
    			data_loaded: false,
    			error: false,

    			rightSideStatus: null,
    			locationBanner: false,
    		};
    	},

    	created() {
    		axios.get('/api/pages/home').then(({data}) => {
                this.dashboard = data;
                this.data_loaded = true;
            }).catch((error) => { 
            	this.data_loaded = true;
            	this.error = true;
            });

            window.addEventListener("resize", this.setRightSideByWidth);
    	},

        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
            ]);  
        },
    	mounted() {
			this.checkAddToHomescreen();

    		this.checkGeo();
			if (document.body.clientWidth < 768) {
				this.hideRightSide();
			}
			else {
				this.showRightSide();
			}
    	},

		destroyed() {
			window.removeEventListener("resize", this.setRightSideByWidth);
			document.getElementById("app").classList.remove('rightside-visible');
		},

    	methods: {
    		checkAddToHomescreen() {
				// variable store event
				window.deferredPrompt = {};
				const install_button = document.querySelector('#install');
				// if the app can be installed emit beforeinstallprompt
				// this event does not fire if the application is already installed
				window.addEventListener('beforeinstallprompt', e => {
					install_button.style.display = 'block'; // show button with display:block;

		    		if (/Mobi|Android/i.test(navigator.userAgent)) {
		    		}
		    		else {					
						e.preventDefault(); // prevent install prompt default event on non-mobile
						
						window.deferredPrompt = e; // store install available event

						// wait for click install button by user
						install_button.addEventListener('click', e => {
							window.deferredPrompt.prompt();
							window.deferredPrompt.userChoice.then(choiceResult => {
								if (choiceResult.outcome === 'accepted') {
									install_button.style.display = 'none';
								} 
								else {
									console.log('User dismissed the prompt');
								}
								window.deferredPrompt = null;
							});
						});
					}
				});

				// if are standalone android OR safari
				if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
					install_button.style.display = 'none';
				}

				// do action when finished install
				window.addEventListener('appinstalled', e => {
				  console.log("success app install!");
				});    
    		},

    		checkGeo() {
    			navigator.geolocation.getCurrentPosition(this.geoSuccess, this.geoError);
    		},

    		geoSuccess() {
    			this.setLocationBanner(false);
    		},

    		geoError() {
    			this.setLocationBanner(true);
    		},

			setLocationBanner(value) {
				this.locationBanner = value;
			},

    		showRightSide() {
				document.getElementById("app").classList.add('rightside-visible');
				document.getElementById("app").classList.remove('rightside-hidden');
    			this.rightSideStatus = true;
    		},

    		hideRightSide() {
    			document.getElementById("app").classList.add('rightside-hidden');
				document.getElementById("app").classList.remove('rightside-visible');    			
    			this.rightSideStatus = false;
    		},

    		setRightSideByWidth() {
				if (document.body.clientWidth < 768) {
					this.hideRightSide();
				}

				if (document.body.clientWidth > 992) {
					this.showRightSide();
				}    			
    		}
    	},

    	computed: mapState(['user'])

    }
</script>